import React from 'react';
import { Multiselect } from 'ui/Multiselect';
import { SingleDateInput } from 'pureUi/SingleDateInput';
import { TextInput } from 'ui/TextInput';
import Checkbox from 'ui/Checkbox';
import { formatDate } from 'utils';
import classNames from 'classnames';

interface IValidationErrors {
  contactEmail?: boolean;
  activeFrom?: boolean;
}

interface IPublishingSettingsSectionProps {
  saveAsDraft: boolean;
  publishFor: 'all_users' | 'internal_users_only' | 'external_users_only';
  activeFrom: string;
  activeTo: string;
  contactEmail: string;
  validationErrors: IValidationErrors;
  updateSaveAsDraft: (saveAsDraft: boolean) => void;
  updatePublishFor: (publishFor: 'all_users' | 'internal_users_only' | 'external_users_only') => void;
  updateActiveFrom: (activeFrom: string) => void;
  updateActiveTo: (activeTo: string) => void;
  updateContactEmail: (contactEmail: string) => void;
}

export const PublishingSettingsSection = ({
  saveAsDraft,
  publishFor,
  activeFrom,
  activeTo,
  contactEmail,
  validationErrors,
  updateSaveAsDraft,
  updatePublishFor,
  updateActiveFrom,
  updateActiveTo,
  updateContactEmail,
}: IPublishingSettingsSectionProps) => {
  const publishForOptions = [
    { value: 'all_users', label: 'All Users' },
    { value: 'internal_users_only', label: 'Internal Users Only' },
    { value: 'external_users_only', label: 'External Users Only' },
  ];

  return (
    <div className="space-y-[10px]">
      {/* Section Title */}
      <span className="font-bold text-[17px] text-gray-800 font-pt-sans">Publishing Settings</span>

      {/* Publishing Settings Row */}
      <div className="flex flex-col md:flex-row md:items-start gap-[8px] md:gap-[40px]">
        {/* Save as Draft Checkbox */}
        <div className="flex flex-col justify-center gap-[10px] min-w-[118px] h-[35px] mt-[20px]">
          <div className="flex items-center gap-2">
            <Checkbox
              id="save-as-draft"
              checked={saveAsDraft}
              onChange={e => updateSaveAsDraft(e.currentTarget.checked)}
            />
            <label htmlFor="save-as-draft" className="font-pt-sans text-[16px] text-gray-800 cursor-pointer">
              Save as Draft
            </label>
          </div>
        </div>

        {/* Publish For Multiselect */}
        <div className="flex flex-col gap-1 w-[220px]">
          <span className="block text-[13px] font-pt-sans text-gray-800 tracking-[0.04em]">Publish For</span>
          <Multiselect
            options={publishForOptions}
            selectedValues={publishFor ? [publishFor] : []}
            onUpdate={values =>
              updatePublishFor(
                (values[0] as 'all_users' | 'internal_users_only' | 'external_users_only') || 'all_users'
              )
            }
            placeholderText="Select users"
            className="bg-ivory"
            itemsClassname="bg-ivory"
            itemCtaClassName="hover:bg-gray-10"
            isSingleSelectMode={true}
            isCloseOnSelect={true}
            hideCheckboxes={true}
          />
        </div>

        {/* Active From Date */}
        <div className="flex flex-col gap-1 w-[140px]" data-validation-id="activeFrom">
          <span className="block text-[13px] font-pt-sans text-gray-800 tracking-[0.04em]">Active From</span>
          <SingleDateInput
            value={activeFrom ? new Date(activeFrom) : null}
            onChange={date => {
              updateActiveFrom(date ? formatDate(date, 'yyyy-MM-dd') : '');
            }}
            enablePastDates={true}
            placeholder=""
            buttonClassName={classNames('!bg-white-hard', { '!border-red-95': validationErrors.activeFrom })}
          />
          {validationErrors.activeFrom && (
            <span className="text-red-95 text-13px leading-2xs font-pt-sans mt-5px">
              Active From is required when not saving as draft
            </span>
          )}
        </div>

        {/* Active To Date */}
        <div className="flex flex-col gap-1 w-[140px]">
          <span className="block text-[13px] font-pt-sans text-gray-800 tracking-[0.04em]">Active To</span>
          <SingleDateInput
            value={activeTo ? new Date(activeTo) : null}
            onChange={date => {
              updateActiveTo(date ? formatDate(date, 'yyyy-MM-dd') : '');
            }}
            enablePastDates={true}
            placeholder=""
            buttonClassName="!bg-white-hard"
          />
        </div>

        {/* Contact Email */}
        <div className="flex flex-col gap-1 w-[300px]" data-validation-id="contactEmail">
          <span className="block text-[13px] font-pt-sans text-gray-800 tracking-[0.04em]">Contact Email</span>
          <TextInput
            id="contact-email"
            value={contactEmail}
            onChange={e => updateContactEmail(e.currentTarget.value)}
            placeholder="info@example.com"
            inputClassName="mt-0"
            errorMessage={validationErrors.contactEmail ? 'Please enter a valid email address' : undefined}
          />
        </div>
      </div>
    </div>
  );
};
